import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';

/**
 * Zahl zaehlt vom letzten Wert zum neuen (Okt 2026, wie im Werbespot:
 * Aura-Zaehler, Belegung einer Session, Match-Ring).
 *
 * Expo-Out wie `--ease-emphasis`: sofort los, lang ausrollend. Beim ersten
 * Render steht die Zahl einfach da (ausser `from` ist gesetzt) — gezaehlt
 * wird nur, wenn sich etwas tatsaechlich aendert. Bei „Bewegung reduzieren“
 * springt sie direkt auf den Endwert.
 */
const expoOut = (p) => (p >= 1 ? 1 : 1 - 2 ** (-10 * p));

export const useCountUp = (value, { duration = 600, from, delay = 0 } = {}) => {
  const reduced = useReducedMotion();
  const target = Number(value);
  const [display, setDisplay] = useState(() => (Number.isFinite(from) ? from : target));
  const lastRef = useRef(Number.isFinite(from) ? from : target);

  useEffect(() => {
    const start = lastRef.current;
    if (reduced || !Number.isFinite(start) || !Number.isFinite(target) || start === target || duration <= 0) {
      lastRef.current = target;
      setDisplay(target);
      return undefined;
    }

    let frame;
    // `delay`: erst zaehlen, wenn das, wozu die Zahl gehoert, sichtbar ist
    // (z.B. ein Logbucheintrag, der sich gerade hereinschiebt).
    const t0 = performance.now() + delay;
    const tick = (now) => {
      if (now < t0) {
        frame = requestAnimationFrame(tick);
        return;
      }
      const p = Math.min(1, (now - t0) / duration);
      setDisplay(start + (target - start) * expoOut(p));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      // Bei einem Wechsel mitten im Zaehlen geht es vom Ziel weiter, nicht
      // vom Zwischenstand: so springt die Zahl nie rueckwaerts.
      lastRef.current = target;
    };
  }, [target, reduced, duration, delay]);

  return Number.isFinite(target) ? display : value;
};

export default useCountUp;
